import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useLocation, useSearchParams } from 'react-router-dom';

import { validateLicensePlate } from '@anwb/poncho/validations/validations-license-plate';
import { isCarFlex } from 'utils/isCarFlex';

import {
	resetAgreedToTerms,
	resetChosenLeaseCar,
	resetIsPricePointSetFromCookie,
	resetRemarks,
	setCheckoutBegins,
	setIsFlex,
	setIsOccasion,
	setLeaseCarData,
} from '../../../store/actions';
import { leaseCarDataIdSelector } from '../../../store/selectors';
import { ErrorResponse, LeaseCarDataResponse, LeaseCarDataResponseOccasion } from '../../../types';
import { getAvailableColorsByUpgrade, isCarAnOccasion } from '../../../utils';
import { fetchLeaseCarData } from '../../../utils/api/fetchLeaseCarData';
import {
	DATA_MISSING_ERROR_MESSAGE,
	INVALID_URL_ERROR_MESSAGE,
	LEASE_CAR_API_SEARCH_PARAM_NAME,
	LEGACY_CAR_SEARCH_PARAM_NAME,
	NOT_AVAILABLE_ERROR_MESSAGE,
} from '../../../utils/constants';
import { trackEvent } from '../../../utils/trackEvent';
import { addConditionParam } from '../utils';

export const useLeaseCarData = (
	currentStepNumber: number
): { carLoading: boolean; errorMessage: string } => {
	const [errorMessage, setErrorMessage] = useState('');
	const [carLoading, setCarLoading] = useState(true);
	const storedLeaseCarDataId = useSelector(leaseCarDataIdSelector);
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const handleFetchCarError = (error: ErrorResponse, leaseCarId: string) => {
		if (error.title === 'DATA_MISSING') {
			setErrorMessage(DATA_MISSING_ERROR_MESSAGE);
		} else if (error.statusCode === 404) {
			if (validateLicensePlate(leaseCarId)) {
				trackEvent({
					event: 'error_info',
					category: 'page',
					type: 'private lease occasion',
					status: '404',
					text: 'deze auto is helaas niet meer beschikbaar',
				});
			}
			setErrorMessage(NOT_AVAILABLE_ERROR_MESSAGE);
		} else {
			setErrorMessage(DATA_MISSING_ERROR_MESSAGE);
		}
	};

	const isOldCarRequestParameterPresent = (leaseCarId: string): boolean => {
		if (!leaseCarId) {
			const oldFlowCmsCarName = searchParams.get(LEGACY_CAR_SEARCH_PARAM_NAME) as string;
			return !!oldFlowCmsCarName;
		}
		return false;
	};

	const resetStore = (): void => {
		dispatch(resetRemarks());
		dispatch(resetAgreedToTerms());
		dispatch(resetChosenLeaseCar());
		dispatch(setCheckoutBegins(false));
		dispatch(resetIsPricePointSetFromCookie());
	};

	const setErrorIfInvalidDataSuppliedByPartner = (
		data: LeaseCarDataResponse | LeaseCarDataResponseOccasion,
		carId: string,
		isOccasionCar: boolean,
		isFlexCar: boolean
	): void => {
		const availableColors = getAvailableColorsByUpgrade(
			data.privateLeaseData.colors,
			parseInt(carId, 10)
		);
		if (
			data.privateLeaseData.pricePoints.length < 1 ||
			(availableColors.length < 1 && !isOccasionCar && !isFlexCar)
		) {
			handleFetchCarError({ title: 'DATA_MISSING' }, carId);
		}
	};

	useEffect(() => {
		let carId = searchParams.get(LEASE_CAR_API_SEARCH_PARAM_NAME) as string;

		if (isOldCarRequestParameterPresent(carId)) {
			setErrorMessage(INVALID_URL_ERROR_MESSAGE);
			return;
		}

		if (storedLeaseCarDataId !== carId) {
			resetStore();
		}

		if (!carId && storedLeaseCarDataId) {
			carId = storedLeaseCarDataId;
		}

		if (currentStepNumber !== 4) {
			if (!carId) {
				setErrorMessage(INVALID_URL_ERROR_MESSAGE);
				return;
			}
			setCarLoading(true);
			(async () => {
				try {
					const data = await fetchLeaseCarData(carId);
					const isOccasionCar = isCarAnOccasion(data);
					const isFlexCar = isCarFlex(data);
					dispatch(setIsFlex(isFlexCar));
					dispatch(setIsOccasion(isOccasionCar));
					addConditionParam(searchParams, isOccasionCar, navigate, location, isFlexCar);
					setErrorIfInvalidDataSuppliedByPartner(data, carId, isOccasionCar, isFlexCar);
					dispatch(setLeaseCarData(data));
					setCarLoading(false);
				} catch (error) {
					console.error(error);
					handleFetchCarError(error as ErrorResponse, carId);
				}
			})();
		} else {
			setCarLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, searchParams]);

	return { errorMessage, carLoading };
};
